import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/i18n/i18n";
import { crDateTime } from "@/lib/dateFormat";
import { Eye, UserPlus, Percent, TrendingUp } from "lucide-react";

const RANGOS = [7, 30, 90];

/** Visitas, inscritos y conversión de la landing de WSS GO (contador propio). */
export const LandingStats = () => {
  const { t } = useI18n();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/live-pass/landing-stats?days=${days}`).then((r) => setD(r.data)).catch(() => {});
  }, [days]);

  if (!d) return null;
  const serie = d.series || [];
  const tope = Math.max(1, ...serie.map((x) => x.people));

  return (
    <Card className="p-5" data-testid="landing-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-orange-500" /> {t("ls_title")}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">{t("ls_sub")}</p>
        </div>
        <div className="flex gap-1">
          {RANGOS.map((n) => (
            <button key={n} onClick={() => setDays(n)} data-testid={`landing-stats-range-${n}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      days === n ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {t("ls_days", { n })}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <Dato icon={Eye} label={t("ls_visitors")} value={d.visitors} testid="ls-visitors" />
        <Dato icon={Eye} label={t("ls_views")} value={d.views} testid="ls-views" />
        <Dato icon={UserPlus} label={t("ls_signups")} value={d.signups} testid="ls-signups" />
        <Dato icon={Percent} label={t("ls_conversion")} value={`${d.conversion}%`}
              testid="ls-conversion" accent />
      </div>

      {d.visitors === 0 ? (
        <p className="text-sm text-slate-500 mt-4" data-testid="ls-empty">{t("ls_empty")}</p>
      ) : (
        <>
          <div className="flex items-end gap-[3px] h-24 mt-5" data-testid="ls-chart">
            {serie.map((x) => (
              <span key={x.day} title={`${x.day} · ${x.people} · ${x.signups}`}
                    className="flex-1 min-w-[3px] rounded-t bg-slate-200 hover:bg-orange-300 transition-colors relative"
                    style={{ height: `${Math.max(3, (x.people / tope) * 100)}%` }}>
                {x.signups > 0 && (
                  <span className="absolute -top-1 left-0 right-0 h-1.5 rounded-t bg-emerald-500" />
                )}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5">{t("ls_chart_note")}</p>

          {!!d.sources?.length && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("ls_sources")}</p>
              <div className="mt-2 space-y-1.5">
                {d.sources.map((s) => (
                  <div key={s.source} className="flex items-center gap-3 text-sm"
                       data-testid={`ls-source-${s.source}`}>
                    <span className="truncate">{s.source === "direct" ? t("ls_direct") : s.source}</span>
                    <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <span className="block h-full rounded-full bg-orange-400"
                            style={{ width: `${(s.visitors / d.visitors) * 100}%` }} />
                    </span>
                    <strong className="text-xs">{s.visitors}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

const Dato = ({ icon: Icon, label, value, testid, accent }) => (
  <div className={`rounded-xl border p-3 ${accent ? "border-orange-200 bg-orange-50" : "border-slate-200"}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" /> {label}
    </p>
    <p className={`text-2xl font-bold mt-1 ${accent ? "text-orange-700" : ""}`} data-testid={testid}>
      {value}
    </p>
  </div>
);
